import { StoreLoadingEnum } from 'src/store/state/types';
import { CreateJobInput, Job, JobOptions, JobInitialState, UpdateExpireDateOptions } from './types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import jobsApi from './api';
import { RootState } from '../..';
import Paginated from '../../../types/paginated';
import { createApiError } from 'src/services/api/helper';
import { MutableRefObject } from 'react';

export interface FetchJobPayload {
  id: string;
  retry?: boolean;
  timeoutRef?: MutableRefObject<number | null>
}

/**
 * Initial State
 */

const initialState: JobInitialState<Paginated<Job, JobOptions>> = {
  loading: StoreLoadingEnum.idle,
  error: null,
  data: {
    items: [],
    totalCount: 0,
    take: 0,
    page: 0,
  },
  jobData: {
    creationDateTime: '',
    jobId: '',
    name: '',
    percentComplete: 0,
    type: '',
    files: [],
    tasks: [],
  },
};

/**
 * Async Actions
 */
let abortController: AbortController | null = null;

const abortPreviousRequest = () => {
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
};

export const fetchJob = createAsyncThunk('job/fetch', async ({ id, timeoutRef }: FetchJobPayload, { dispatch, rejectWithValue }) => {
  try {
    const response = await jobsApi.fetchJob(id);

    const retryAfter = response.headers['retry-after'];

    if (response.status === 202 && retryAfter) {
      const retryAfterMs = parseInt(retryAfter, 10) * 1000;
      if (timeoutRef && timeoutRef.current !== 0) {
        // @ts-ignore
        timeoutRef.current = setTimeout(() => {
          dispatch(fetchJob({id, retry: true,  timeoutRef}));
        }, retryAfterMs);
      }
    }

    return { jobData: { ...response.data } };
  } catch (e) {
    if (e instanceof Error) {
      return rejectWithValue({ message: e.message });
    } else {
      return rejectWithValue({ message: 'An unknown error occurred' });
    }
  }
});

export const fetchJobs = createAsyncThunk('jobs/fetch', async (options: JobOptions, thunkApi) => {
  abortPreviousRequest();
  try {
    const response = await jobsApi.fetchJobs(options, { signal: abortController?.signal });
    return { data: { ...response.data, options } };
  } catch (e: unknown) {
    if ((e as {name: string}).name !== "CanceledError") {
      return thunkApi.rejectWithValue(createApiError(e as any));
    }
  }
});

export const createJob = createAsyncThunk(
  'jobs/create',
  async (input: CreateJobInput, thunkApi) => {
    try {
      const response = await jobsApi.createJob(input);
      return response.data;
    } catch (e) {
      return thunkApi.rejectWithValue(createApiError(e as any));
    }
  },
);

export const updateJob = createAsyncThunk(
  'jobs/update',
  async (input: UpdateExpireDateOptions, thunkApi) => {
    try {
      const response = await jobsApi.updateJob(input);
      return response.data;
    } catch (e) {
      return thunkApi.rejectWithValue(createApiError(e as any));
    }
  },
);

export const deleteJob = createAsyncThunk('jobs/delete', async (id: string, thunkApi) => {
    try {
      await jobsApi.deleteJob(id);
    } catch (e) {
      return thunkApi.rejectWithValue(createApiError(e as any));
    }
  },
);

export const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    clearJobs: (state) => {
      state.data = {
        items: [],
        totalCount: 0,
        take: 0,
        page: 0,
      };
    },
    clearJob: (state) => {
      state.jobData =  {
        creationDateTime: '',
        jobId: '',
        name: '',
        percentComplete: 0,
        type: '',
        files: [],
        tasks: [],
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobs.pending, (state, action) => {
        if (!action.meta.arg.isRefresh) {
          state.loading = StoreLoadingEnum.pending;
        }
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        if (action.payload) {
          const { data } = action.payload;
          state.data = data;
          state.loading = StoreLoadingEnum.succeeded;

          if (!action.meta.arg.isRefresh) {
            state.loading = StoreLoadingEnum.succeeded;
          }
        }
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        if (!action.meta.arg.isRefresh) {
          state.loading = StoreLoadingEnum.failed;
        }
      })
      .addCase(fetchJob.pending, (state, action) => {
        if (!action.meta.arg.retry) {
          state.loading = StoreLoadingEnum.pending;
        }
      })
      .addCase(fetchJob.fulfilled, (state, action) => {
        state.loading = StoreLoadingEnum.succeeded;
        const { jobData } = action.payload;
        state.jobData = jobData;
      })
      .addCase(fetchJob.rejected, (state) => {
        state.loading = StoreLoadingEnum.failed;
      })
      .addCase(createJob.pending, (state) => {
        state.loading = StoreLoadingEnum.pending;
      })
      .addCase(createJob.fulfilled, (state) => {
        state.loading = StoreLoadingEnum.succeeded;
      })
      .addCase(deleteJob.pending, (state) => {
        state.loading = StoreLoadingEnum.pending;
      })
      .addCase(deleteJob.fulfilled, (state) => {
        state.loading = StoreLoadingEnum.succeeded;
      })
      .addCase(deleteJob.rejected, (state) => {
        state.loading = StoreLoadingEnum.failed;
      })
      .addCase(updateJob.pending, (state) => {
        state.loading = StoreLoadingEnum.pending;
      })
      .addCase(updateJob.fulfilled, (state) => {
        state.loading = StoreLoadingEnum.succeeded;
      })
      .addCase(updateJob.rejected, (state) => {
        state.loading = StoreLoadingEnum.failed;
      });
  },
});

export const jobsSelector = (state: RootState) => state.jobs.data;
export const jobSelector = (state: RootState) => state.jobs.jobData;
export const isLoadingSelector = (state: RootState) =>
  state.jobs.loading === StoreLoadingEnum.pending;
export const { clearJobs, clearJob } = jobsSlice.actions;
